import { useEffect, useState, useRef, useCallback } from "react";
import { useWall } from "wall/hooks/useWall";
import ImagesSlideshow from "./ImagesSlideshow";
import MessagesSlideshow from "./MessagesSlideshow";
import { wallTemplate } from "wall/types/wall.model";
import { WallService } from "services/wall.service";
import { MessageService } from "services/message.service";
import { Wall } from "wall/types/wall.model";
import { Message } from "wall/types/message.model";

function WallCastView({ mode }: { mode: "messages" | "images" }) {
  const wall = useWall()!;
  const [messages, setMessages] = useState<Message[]>([]);
  const [updatedMessages, setUpdatedMessages] = useState<Message[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideshowStatus, setSlideshowStatus] = useState<
    "PLAY" | "PAUSE" | undefined
  >(undefined);
  const [slideshowInterval, setSlideshowInterval] = useState(5000);
  const [error, setError] = useState<string | null>(null);

  const POLLING_INTERVAL = 5000; // 5 seconds
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wallService = useRef(new WallService());
  const messageService = useRef(new MessageService());

  // Function to update server with current slideshow state
  const updateServerStatus = useCallback(
    async (index: number) => {
      try {
        await wallService.current.updateWallStatus({
          wallId: wall.id,
          currentSlideshowIndex: index,
          totalMessages: updatedMessages.length,
        });
      } catch (error) {
        console.error("Failed to update server status:", error);
      }
    },
    [wall.id, updatedMessages.length],
  );

  // Function to fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const messagesData = await messageService.current.listMessages({
        wallId: wall.id,
      });
      setMessages(messagesData);
      setError(null);
      return messagesData;
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load messages");
      return [];
    }
  }, [wall.id]);

  // Function to fetch wall configuration (status, interval, etc.)
  const fetchWallConfig = useCallback(async () => {
    try {
      const data = await wallService.current.getWallById({ wallId: wall.id });
      const wallData = data as unknown as Wall;

      console.log("Fetched wall config:", {
        status: wallData?.slideshow?.status,
        interval: wallData?.slideshow?.slideshowInterval,
        serverIndex: wallData?.slideshow?.currentSlideshowIndex,
      });

      // Update slideshow configuration
      setSlideshowStatus(wallData?.slideshow?.status);
      setSlideshowInterval(wallData?.slideshow?.slideshowInterval || 5000);

      return wallData;
    } catch (error) {
      console.error("Error fetching wall config:", error);
      return null;
    }
  }, [wall.id]);

  // Function to process message updates
  const processMessageUpdates = useCallback((newMessages: Message[]) => {
    const newFilteredMessages = newMessages.filter(
      (message) => message.approved,
    );

    setUpdatedMessages((prevUpdatedMessages) => {
      // Create a map of existing messages by ID for efficient lookup
      const existingMessagesMap = new Map(
        prevUpdatedMessages.map((msg) => [msg.id, msg]),
      );

      // Find new messages that weren't in the previous list
      const newlyAddedMessages = newFilteredMessages.filter(
        (msg) => !existingMessagesMap.has(msg.id),
      );

      // Keep existing messages that are still in the new filtered list
      const stillExistingMessages = prevUpdatedMessages.filter((msg) =>
        newFilteredMessages.some((newMsg) => newMsg.id === msg.id),
      );

      // Combine existing messages with new ones (new ones at the end)
      const combined = [...stillExistingMessages, ...newlyAddedMessages];

      console.log("Message updates:", {
        previousCount: prevUpdatedMessages.length,
        newFilteredCount: newFilteredMessages.length,
        newlyAdded: newlyAddedMessages.length,
        finalCount: combined.length,
      });

      return combined;
    });
  }, []);

  // Combined polling function
  const pollServer = useCallback(async () => {
    console.log("Polling server...");
    const [messagesData, wallConfig] = await Promise.all([
      fetchMessages(),
      fetchWallConfig(),
    ]);
    void wallConfig;

    // Process message updates with the new strategy
    processMessageUpdates(messagesData);
  }, [fetchMessages, fetchWallConfig, processMessageUpdates]);

  // Setup server polling
  useEffect(() => {
    console.log("Setting up server polling...");

    // Poll immediately
    pollServer();

    // Setup interval polling
    pollingIntervalRef.current = setInterval(pollServer, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pollServer]);

  // Handle index adjustments when updatedMessages changes
  useEffect(() => {
    if (updatedMessages.length > 0) {
      setCurrentIndex((prevIndex) => {
        // If current index is out of bounds, reset to 0
        if (prevIndex >= updatedMessages.length) {
          console.log(
            "Index out of bounds after message updates, resetting to 0",
          );
          return 0;
        }
        return prevIndex;
      });
    } else {
      setCurrentIndex(0);
    }
  }, [updatedMessages.length]);

  // Handle index changes - update server when index changes
  useEffect(() => {
    if (updatedMessages.length > 0) {
      console.log(`Index changed to ${currentIndex}, updating server...`);
      updateServerStatus(currentIndex);
    }
  }, [currentIndex, updateServerStatus, updatedMessages.length]);

  // Handle slideshow advancement
  const handleSlideAdvance = useCallback(() => {
    if (updatedMessages.length > 1) {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % updatedMessages.length;
        console.log(`Slideshow advancing: ${prevIndex} → ${nextIndex}`);
        return nextIndex;
      });
    }
  }, [updatedMessages.length]);

  console.log("=== WALLCAST VIEW STATE ===");
  console.log("Total messages:", messages.length);
  console.log("Updated messages:", updatedMessages.length);
  console.log("Current index:", currentIndex);
  console.log("Slideshow status:", slideshowStatus);
  console.log("Slideshow interval:", slideshowInterval);

  if (error) {
    return <div className="text-red-500 p-4">Error: {error}</div>;
  }

  if (updatedMessages.length === 0) {
    return <div className="text-gray-500 p-4">No approved messages found</div>;
  }

  return (
    <>
      {mode === "images" ? (
        <ImagesSlideshow messages={updatedMessages} />
      ) : (
        <MessagesSlideshow
          messages={updatedMessages}
          template={wall.template || ("bandeau_horizontal" as wallTemplate)}
          currentIndex={currentIndex}
          onSlideAdvance={handleSlideAdvance}
          slideshowStatus={slideshowStatus}
          slideshowInterval={slideshowInterval}
          wall={wall}
        />
      )}
    </>
  );
}

export default WallCastView;
